import React from "react";
import { Calendar, Clock, Briefcase, PieChart, AlertOctagon } from "lucide-react";
import ChapterShell from "@/tutorial/ChapterShell";
import {
  Section,
  Formula,
  TipBox,
  InfoBox,
  Quote,
} from "@/tutorial/components/TutorialComponents";

const KPIS = [
  {
    icon: Calendar,
    name: "Jours sacrifiés / an",
    desc:
      "Quantifie en journées la part annuelle de votre vie absorbée par l'État.",
    thresholds: [
      { color: "bg-emerald-500", label: "≤ 90" },
      { color: "bg-amber-500", label: "90 – 180" },
      { color: "bg-red-500", label: "> 180" },
    ],
  },
  {
    icon: Clock,
    name: "Mois de vie / an",
    desc:
      "Convertit les jours sacrifiés en mois — repère intuitif sur 12 mois.",
    thresholds: [
      { color: "bg-emerald-500", label: "≤ 3" },
      { color: "bg-amber-500", label: "3 – 6" },
      { color: "bg-red-500", label: "> 6" },
    ],
  },
  {
    icon: Briefcase,
    name: "Années de carrière",
    desc:
      "Cumul vitaliste sur 40 ans de carrière : combien d'années totales reviennent à l'État.",
    thresholds: [
      { color: "bg-emerald-500", label: "≤ 8" },
      { color: "bg-amber-500", label: "8 – 16" },
      { color: "bg-red-500", label: "> 16" },
    ],
  },
  {
    icon: PieChart,
    name: "% de vie fiscale",
    desc:
      "Part annuelle de votre temps de vie consacrée à honorer la fiscalité.",
    thresholds: [
      { color: "bg-emerald-500", label: "≤ 25 %" },
      { color: "bg-amber-500", label: "25 – 50 %" },
      { color: "bg-red-500", label: "> 50 %" },
    ],
  },
  {
    icon: AlertOctagon,
    name: "Années hypothéquées (dette)",
    desc:
      "Vies futures préemptées par la dette publique pour rembourser le passé.",
    thresholds: [
      { color: "bg-emerald-500", label: "≤ 5" },
      { color: "bg-amber-500", label: "5 – 15" },
      { color: "bg-red-500", label: "> 15" },
    ],
  },
];

export default function Chapter4() {
  return (
    <ChapterShell id={4}>
      <Section id="section-4-1" eyebrow="4.1" title="La formule centrale">
        <p>
          Toute l'analyse vitaliste tient dans une équation simple :
        </p>
        <Formula caption="Vitae publica = vita publica · Tranches de vie = Prélèvements / Salaire journalier">
          Jours de vie = Total prélèvements ÷ Salaire journalier
        </Formula>
        <p>
          Avec, comme socle dérivé du chapitre 3, la définition canonique du
          salaire journalier de référence :
        </p>
        <Formula caption="Définition vitaliste — la vie ne s'arrête pas le week-end">
          Salaire journalier = Salaire mensuel ÷ 30
        </Formula>
        <p>
          Tout comme l'équation d'Einstein <em>E = mc²</em> ramène l'énergie à
          une masse, la formule vitaliste ramène la monnaie publique à la
          quantité de vie qu'elle représente. C'est un changement de référentiel,
          pas un changement d'objet.
        </p>
        <Quote author="Manifeste vitaliste">
          La monnaie n'est qu'une représentation conventionnelle ; le temps de
          vie, lui, est inaliénable.
        </Quote>
      </Section>

      <Section id="section-4-2" eyebrow="4.2" title="Lire les 5 indicateurs KPI">
        <p>
          Le moteur vitaliste produit cinq indicateurs synthétiques. Chaque
          carte affiche son seuil de référence selon trois zones de gravité.
        </p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {KPIS.map((k) => {
            const Icon = k.icon;
            return (
              <div
                key={k.name}
                className="rounded-xl border border-border bg-card p-4"
              >
                <div className="flex items-start gap-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-blue/10 text-brand-blue dark:text-brand-gold">
                    <Icon className="h-5 w-5" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold leading-tight">{k.name}</div>
                    <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                      {k.desc}
                    </p>
                  </div>
                </div>
                <div className="mt-3 grid grid-cols-3 gap-1.5 text-[10px] font-semibold">
                  {k.thresholds.map((t, i) => (
                    <div key={i} className="text-center">
                      <div className={`h-1.5 rounded-full ${t.color}`} />
                      <div className="mt-1 text-muted-foreground">{t.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </Section>

      <Section
        id="section-4-3"
        eyebrow="4.3"
        title="Le budget national vu en vies"
      >
        <p>
          Au-delà du contribuable individuel, l'analyse vitaliste s'applique à
          l'agrégat national. Reprenons un cas mauritanien fictif :
        </p>
        <Formula caption="Hypothèses : budget 487 Mds UM · salaire journalier moyen 1 667 UM">
          487 000 000 000 ÷ 1 667 ≈ 292 000 000 jours de vie
        </Formula>

        <div className="rounded-lg border-2 border-red-700/40 bg-gradient-to-br from-red-900 via-red-800 to-red-900 p-8 text-center text-white shadow-lg">
          <div className="text-[11px] font-bold uppercase tracking-widest text-red-200">
            Lecture vitaliste du budget
          </div>
          <div className="mt-3 font-judicial text-3xl font-black leading-tight sm:text-4xl">
            ≈ 800 000 années de vie humaine
          </div>
          <div className="mt-2 text-sm text-red-100">
            mobilisées chaque année pour faire fonctionner l'État.
          </div>
        </div>

        <InfoBox title="Lecture comparative">
          Cette approche permet de poser des comparaisons inédites : par
          exemple, le budget de la défense d'un pays exprimé en années de vie
          travaillées par sa population active.
        </InfoBox>

        <TipBox title="Pour les conférences">
          Cette infographie chiffre-choc fonctionne très bien en mode
          présentation (chapitre 9). Pensez à l'inclure dans votre rapport
          exporté pour marquer durablement votre auditoire.
        </TipBox>
      </Section>
    </ChapterShell>
  );
}
